import { Dispatch, SetStateAction, useRef } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { theseus, theseus2 } from '../../../../api/theseus'
import { selectCurrentProject } from '../../../../modules/missionControl/selectors'
import { selectUser } from '../../../../modules/user/selectors'
import { stopPropagation } from '../../../EmbeddableModal'
import { ThemedButton } from '../../../ThemedButton'
import { Box } from '../../../UI/Box'
import { SubTask } from '../../types'
import useBotPrevention from '../../../BotPrevention/useBotPrevention'

export type Props = {
  isSubmitting: boolean
  hasError: boolean
  setIsSuccess: (bool: boolean) => void
  setIsSubmitting: (bool: boolean) => void
  taskId: number
  subtaskId: number
  channelName?: string
  setErrors: Dispatch<SetStateAction<string[]>>
  type: 'youtube' | 'spotify' | 'tiktok' | 'instagram'
  onClose: (isError?: boolean) => void
  isVisible: boolean
  task: SubTask
  updateTaskSubmissions: () => Promise<unknown>
}

type ContainerProps = { isOpen?: boolean; isVisible?: boolean }

const Container = styled(Box)<ContainerProps>`
  @media screen and (max-width: 1000px) {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    button {
      margin: 16px 0 8px;
    }
  }
  ${({ isVisible }) => !isVisible && 'display: none;'}
  .file-uploader {
    display: none;
  }
  a {
    width: 100%;
    margin: auto;
  }
  button {
    margin: 0 !important;
  }
`

export type ReqBody = {
  userId: number
  project: number
}

const UploadTask = ({
  isSubmitting,
  setIsSuccess,
  setIsSubmitting,
  taskId,
  subtaskId,
  channelName,
  setErrors,
  type,
  onClose,
  isVisible,
  task,
  updateTaskSubmissions,
}: Props) => {
  const hiddenFileInput = useRef<HTMLInputElement>(null)

  const currentProject = useSelector(selectCurrentProject)
  const lithiumAccount = useSelector(selectUser)

  const { turnstileToken, Turnstile } = useBotPrevention()

  if (!currentProject) return <></>

  const stringValueRef: {
    [key: string]: string
  } = {
    instagram_verification: 'Instagram',
    tiktok_verification: 'TikTok',
    youtube_verification: 'YouTube',
    spotify_verification: 'Spotify',
  }

  const { accountHandleOrTweetUrl, imageVerification, visitUrl } = task
  if (!imageVerification) return <></>

  const verificationString = stringValueRef[imageVerification]

  if (!verificationString) return <></>

  const isYoutube = imageVerification === 'youtube_verification'
  const isTiktok = imageVerification === 'tiktok_verification'

  const urlString =
    visitUrl ??
    `https://www.${verificationString.toLowerCase()}.com/${
      isYoutube || isTiktok ? '@' : ''
    }${accountHandleOrTweetUrl}`

  const handleUpload = async (file: File) => {
    if (!file) return
    if (!taskId || !lithiumAccount?.id) throw new Error('No account or task id')
    if (!currentProject?.id) throw new Error('No project id')

    const filename = `task-proof-${new Date().getTime()}`
    const { width, height } = await getDimensions(file)

    const formData = new FormData()
    formData.append('image', file)
    formData.append('name', filename)
    formData.append('width', width.toString())
    formData.append('height', height.toString())

    let imageUrl

    try {
      const response = await theseus2.post(
        `/project/upload/${currentProject.id}/generic/b2c/${lithiumAccount.id}`,
        formData,
      )

      const {
        data: { uploadedImage },
      } = response

      imageUrl = uploadedImage.url
    } catch (response) {
      console.error(response)
      toast.error('Something went wrong')
      setErrors([response.data?.message])
      setIsSubmitting(false)
      return
    }

    try {
      const requestBody = {
        image_url: imageUrl,
        userId: lithiumAccount.id,
        channel_name: channelName,
        projectId: currentProject.id,
        id: taskId,
        subtaskId,
        name: filename,
        type,
        turnstileToken,
      }

      const res = await theseus.post(
        `task-submission/b2c/validate-image`,
        requestBody,
      )

      if (res.status !== 200) {
        const data = (res as any)?.response?.data
        setErrors(data.errors)
        setIsSubmitting(false)
        return
      }

      completeSubmission()
    } catch (error: any) {
      console.error(error)
      toast.error('Something went wrong')
      if (error.response?.status !== 400) return
      const { data } = error.response
      setErrors(data.errors)
      setIsSubmitting(false)
    }
  }

  const completeSubmission = async () => {
    try {
      setIsSubmitting(false)
      setIsSuccess(true)
      await updateTaskSubmissions()
      setTimeout(() => {
        onClose()
      }, 4000)
    } catch (error) {
      console.error(error)
      toast.error('Error completing submission')
    }
  }

  const startUpload = (e: any) => {
    stopPropagation(e)
    e.target.value = null
    if (isSubmitting || !hiddenFileInput?.current) return
    const blank = new DataTransfer()
    hiddenFileInput.current.files = blank.files
    hiddenFileInput.current.click()
  }

  const onImageChange = async (event: any) => {
    const fileUploaded = await event.target.files[0]
    if (!fileUploaded) return
    setIsSubmitting(true)

    setErrors([])
    handleUpload(fileUploaded)
  }

  const isLoggedIn = Boolean(lithiumAccount?.id)

  function jsUrlRedirect() {
    if (typeof window === 'undefined') return
    const stringWithProtocolIfNeeded = urlString.startsWith('http')
      ? urlString
      : `https://${urlString}`
    window.open(stringWithProtocolIfNeeded, '_blank')
  }

  return (
    <Box dc ac g={32}>
      <Container ac c fu isVisible={isVisible} g={16}>
        <input
          type="file"
          accept="image/png,image/jpg,image/jpeg,image/gif,image/webp"
          value={''}
          className="file-uploader"
          onChange={onImageChange}
          ref={hiddenFileInput}
        />
        <ThemedButton disabled={isSubmitting} onClick={jsUrlRedirect}>
          Visit {getTaskTypeHandle(type)}
        </ThemedButton>
        <ThemedButton
          onClick={isLoggedIn ? startUpload : null}
          disabled={isSubmitting}
          $styleType="primary"
        >
          Upload Screenshot
        </ThemedButton>
      </Container>

      <Turnstile />
    </Box>
  )
}

export default UploadTask

function getTaskTypeHandle(taskType: string) {
  return {
    youtube: 'YouTube',
    spotify: 'Spotify',
    tiktok: 'TikTok',
    instagram: 'Instagram',
  }[taskType]
}

export function getDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.width, height: img.height })
    }
    img.onerror = (error) => {
      reject(new Error('Unable to retrieve image dimensions.'))
    }
    img.src = URL.createObjectURL(file)
  })
}

export function getBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
  })
}
